import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import { getConversationList } from "@/services/message/message.service";
import type {
  GetConversationListRequest,
  ConversationItem,
} from "@/interface/message.types";
import { useChatUnreadStore } from "@/store/chat-unread-store";

/**
 * React Query hook to fetch and manage conversation list
 * Transforms API response to match UI component needs
 * Supports project-based filtering via URL query parameter
 *
 * @param isLoggedIn - Whether user is logged in (enables/disables query)
 * @param request - Optional request parameters for pagination, filtering, etc.
 * @returns React Query result with transformed conversation list data
 */
export const useConversationList = (
  isLoggedIn: boolean | null,
  request?: Partial<GetConversationListRequest>
) => {
  const isUnread = useChatUnreadStore((state) => state.isUnread);
  const searchParams = useSearchParams();
  const projectId = searchParams.get("project_id");

  // Build request with project filter if project_id exists in URL
  const requestWithFilter: Partial<GetConversationListRequest> = {
    ...request,
    // filters: projectId ? `projectId == ${projectId}` : request?.filters || "" // commented out for now ,
    filters: "",
  };

  return useQuery({
    queryKey: ["conversations", "Homeowner", requestWithFilter, projectId],
    queryFn: () => getConversationList(requestWithFilter),
    enabled: !!isLoggedIn,
    select: (response): ConversationItem[] => {
      // Transform API response to UI component format
      return response.data.coversationData.map((item) => ({
        id: item.proposalId, // Use proposalId as unique identifier
        contractorName: item.userDetail.username,
        contractorId: item.userDetail.id,
        projectTitle: item.projectTitle,
        projectId: item.projectId,
        proposalId: item.proposalId,
        // Fallback to projectTitle if lastMessage is empty
        message: item.lastMessage || item.projectTitle,
        timestamp: item.messagedDay, // Relative time already formatted
        rawTimestamp: item.messagedDay,
        isUnread: isUnread(item.proposalId), // Merge persisted unread state
      }));
    },
    staleTime: 0, // Set to 0 to ensure fresh data on entry (Socket handles live updates thereafter)
    refetchOnWindowFocus: true,
    retry: 2,
  });
};
